/* What the Client → Project → Task pick-lists offer (timer and the add/edit entry window).
   Only active clients that have an active project, only that client's projects, only that project's tasks; the ones this
   person used most recently (from the recent weeks on this device) come first. Old client names (aliases) are searchable.
   Members and the bookkeeper are offered only the projects assigned to them (and so only clients that have one); owner and
   admins every project. `keep` (the value already chosen, e.g. an old entry's project) always stays listed. */
import {State, byId, mayRecord, usesAllProjects} from '../state.ts';
import type {ComboOption} from './combo.ts';

const RECENT = 6;
function recent(memberId: string, pick: (projectId: string, taskId: string) => string): string[] {
  const out: string[] = [];
  const list = State.entries.filter(e => e.memberId === memberId).sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  for (const e of list) { const id = pick(e.projectId, e.taskId); if (id && !out.includes(id)) out.push(id); if (out.length >= RECENT) break; }
  return out;
}
export const clientOf = (projectId: string) => byId(State.projects, projectId)?.clientId || '';
export const clientLabel = (id: string) => byId(State.clients, id)?.name || '';
export const projectLabel = (id: string) => byId(State.projects, id)?.name || '';
export const taskLabel = (id: string) => byId(State.tasks, id)?.name || '';

const keeps = (keep: string | string[]) => (Array.isArray(keep) ? keep : [keep]).filter(Boolean);
/** what the client list says when it is empty because nothing is assigned to this person */
export const noProjectsText = () => usesAllProjects() ? '' : 'No projects are assigned to you yet. Ask the owner to add you to a project.';
export function clientOptions(memberId: string, keep: string | string[] = ''): ComboOption[] {
  const k = keeps(keep);
  const withProjects = new Set(State.projects.filter(p => p.active && mayRecord(p.id)).map(p => p.clientId));
  const rec = recent(memberId, p => clientOf(p));
  return State.clients.filter(c => (c.active && withProjects.has(c.id)) || k.includes(c.id))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(c => ({id: c.id, label: c.name, keywords: c.aliases.join(' '), recent: rec.includes(c.id)}))
    .sort((a, b) => a.recent && b.recent ? rec.indexOf(a.id) - rec.indexOf(b.id) : 0);
}
export function projectOptions(clientId: string, memberId: string, keep: string | string[] = ''): ComboOption[] {
  const k = keeps(keep);
  const rec = recent(memberId, p => clientOf(p) === clientId ? p : '');
  return State.projects.filter(p => p.clientId === clientId && ((p.active && mayRecord(p.id)) || k.includes(p.id)))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(p => ({id: p.id, label: p.name, recent: rec.includes(p.id)}))
    .sort((a, b) => a.recent && b.recent ? rec.indexOf(a.id) - rec.indexOf(b.id) : 0);
}
export function taskOptions(projectId: string, memberId: string, keep = ''): ComboOption[] {
  const rec = recent(memberId, (p, t) => p === projectId ? t : '');
  return [{id: '', label: 'None'} as ComboOption].concat(State.tasks.filter(t => t.projectId === projectId && (t.active || t.id === keep))
    .sort((a, b) => a.name.localeCompare(b.name)).map(t => ({id: t.id, label: t.name, recent: rec.includes(t.id)})));
}
/** after a client is chosen: keep the project if it belongs to it, otherwise pick its only project (or none) */
export function projectAfterClient(clientId: string, projectId: string): string {
  if (clientOf(projectId) === clientId) return projectId;
  const ps = State.projects.filter(p => p.clientId === clientId && p.active && mayRecord(p.id));
  return ps.length === 1 ? ps[0].id : '';
}
